// @flow
import React, {Component} from 'react';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import GetHelp from '@ccxi/ccxi-shared-components/src/GetHelp';
import Help from '@react/react-spectrum/Icon/Help';
import axios from 'axios';
import HelpPopoverUtil from '../../../../utils/AppPicker/HelpPopoverUtil';
import AppPickerUtil from '../../../../utils/AppPicker/AppPickerUtil';
import AppsUtil from '../../../../utils/AppsUtil';
import CCDUtil from '../../../../utils/CCDUtil';
import DeviceDetectionUtil from '../../../../utils/DeviceDetectionUtil';
import DownloadUtil from '../../../../utils/DownloadUtil';
import CSSUtil from '../../../../utils/CSSUtil';
import URL from '../../../../utils/Url';
import Feedback from './Feedback';
import AutoDownload from './AutoDownload';
import './GetHelp.scss';
import {getContentfulPath} from '../../../../utils/contentfulDataPathUtil';
import {getAEMPath} from '../../../../utils/aemPathUtil';
import getConfigApps from '../../../../config';
import ContentParser from '@ccxi/ccxi-apps/src/utils/contentParser';
import {INLINES} from '@contentful/rich-text-types';
import {documentToHtmlString} from '@contentful/rich-text-html-renderer';
import {getLocalizedURL} from '@intl/adobe-locales';
import uuid from 'uuid';
import AppPickerAnalytics from '../../../../analytics/AppPickerAnalytics';
import {
	QUESTION_IDS,
	GET_HELP_STRINGS,
	LIST_IDS,
	TAGS,
	GET_HELP_ANSWER_LINKS,
	PLACEHOLDER_STRINGS,
	CCPP_PLAN,
	CCPPN_PLAN,
	ALL_APPS_PLAN,
	SINGLE_APP_PLAN,
	SUBSTANCE_COLLECTION_PLAN,
	SUBSTANCE_TEXTURING_PLAN,
	GET_HELP_OPEN_SOURCES,
	GET_HELP_CALLBACK,
	DOWNLOAD_VIA_CCD_QUERY_PARAM,
	LOGGED_OUT_CAMPAIGN_ENV_NAME
} from '../../../../constants/AppPicker/appPickerConstants';
import {DownloadWorkflow} from '../../../../constants/appsConstants';
import DownloadCoachMark from '../../../../components/DownloadCoachMark/DownloadCoachMark';
import type {ElementRef} from 'react';

const ANALYTICS = {
	RENDER: 'render',
	ITEM_CLICK: 'item-click',
	FALLBACK: 'fallback',
	CLICK: 'click',
	LINK_CLICK: 'link-click',
	CAMPAIGN: 'Campaign CTA'
};

type Props = {
	intl: Object,
	locale: string,
	history: Object,
	environment: string
};

type AnalyticObjectInterface = {
	subcategory: string,
	subtype?: ?string,
	value?: ?string,
	type?: ?string,
	workflow?: ?string,
	guid?: ?string
};

type State = {
	defaultSelectedItemId: ?string,
	config: Object[],
	showAutoDownload: boolean,
	isDataAvailable: boolean, /* Check to ensure that the Widget Icon is shown only once CVS data is fetched */
	shouldFocusFirstListItem: boolean	// focus item in first list when open through accessibility
}

export default class AllAppsGetHelpPopover extends Component<Props, State> {
	isCCLogoClick: boolean;
	isInactivity: boolean;
	isFooterLinkClick: boolean;
	isGetHelpDownloadPageClick: boolean;
	isGetHelpLoggedOutCampaignPageClick: boolean;
	isGetHelpLoggedOutCampaignCCLogoClick: boolean;
	configUpdateArguments: ?Object;
	callbacks: Object;
	helpPopover: ?ElementRef<typeof OverlayTrigger>;
	scenarioTagList: Object;
	getHelpQuestions: ?Array<Object>;

	constructor(props: Props) {
		super(props);
		this.state = {
			defaultSelectedItemId: null,
			config: [],
			showAutoDownload: false,
			isDataAvailable: false,
			shouldFocusFirstListItem: false
		};

		/* Variable that tracks where the Get Help was openend from */
		this.isInactivity = false;
		this.isCCLogoClick = false;
		this.isFooterLinkClick = false;
		this.isGetHelpDownloadPageClick = false;
		this.isGetHelpLoggedOutCampaignPageClick = false;
		this.isGetHelpLoggedOutCampaignCCLogoClick = false;

		this.configUpdateArguments = null; // store arguments of config update call before setting intial config.
		this.callbacks = {}; // Contains a map of callback id and corresponding callback function
		HelpPopoverUtil.updateHelpPopoverConfig = this.updateHelpPopoverConfig.bind(this);
		HelpPopoverUtil.openGetHelpPopover = this.show.bind(this);
		HelpPopoverUtil.closeGetHelpPopover = this.hide.bind(this);
		HelpPopoverUtil.openAutoDownloadPopover = this.openAutoDownloadPopover.bind(this);
		HelpPopoverUtil.setCallback = this.setCallback.bind(this);
	}

	openAutoDownloadPopover() {
		this.setState({showAutoDownload: true}, () => this.show());
	}

	show(openSource: ?string, autoFocusListItem?: boolean) {
		switch (openSource) {
			case GET_HELP_OPEN_SOURCES.INACTIVITY:
				this.isInactivity = true;
				break;
			case GET_HELP_OPEN_SOURCES.CC_LOGO_CLICK:
				this.isCCLogoClick = true;
				break;
			case GET_HELP_OPEN_SOURCES.FOOTER_LINK_CLICK:
				this.isFooterLinkClick = true;
				break;
			case GET_HELP_OPEN_SOURCES.GET_HELP_DOWNLOAD_PAGE:
				this.isGetHelpDownloadPageClick = true;
				break;
			case GET_HELP_OPEN_SOURCES.LOGGED_OUT_CAMPAIGN_GET_HELP_CLICK:
				this.isGetHelpLoggedOutCampaignPageClick = true;
				break;
			case GET_HELP_OPEN_SOURCES.LOGGED_OUT_CAMPAIGN_CCLOGO_CLICK:
				this.isGetHelpLoggedOutCampaignCCLogoClick = true;
				break;
		}
		if (this.helpPopover) {
			if (autoFocusListItem) {
				const popOver = document.getElementsByClassName('getHelpPopover')[0];
				if (popOver) {
					this.helpPopover.hide();
					return;
				}
				this.setState({shouldFocusFirstListItem: true});
			}
			this.helpPopover.show();
		}
	}


	hide() {
		if (this.helpPopover) {
			this.helpPopover.hide();
			requestAnimationFrame(() => {
				this.setState({showAutoDownload: false});
			});
		}
	}

	handleAnalyticsEvent(eventType: string, section: ?string, questionId: ?string, extUrl: ?string, guid: ?string) {
		const analytics = new AppPickerAnalytics(this.props.environment);
		analytics.initAnalytics();
		const event: AnalyticObjectInterface = {
			subcategory: 'Help'
		};
		const content = {};
		const ui = {
			view_type: analytics.getPageTypeString() // eslint-disable-line camelcase
		};
		let env;
		switch (eventType) {
			case ANALYTICS.RENDER:
				event.type = 'render';
				event.subtype = 'modal';
				if (this.isInactivity) {
					event.subcategory = 'Inactivity';
					event.subtype = 'help-modal';
					event.value = '45 seconds';
				}
				if (this.isCCLogoClick) {
					event.subtype = 'help-modal';
					event.subcategory = 'CC Logo Click';
				}
				if (this.isFooterLinkClick) {
					event.subtype = 'help-modal';
					event.subcategory = 'Welcome';
				}
				if (this.isGetHelpDownloadPageClick) {
					event.subtype = 'help-modal';
					event.subcategory = 'Get Help Link Download Page';
				}
				if (this.isGetHelpLoggedOutCampaignPageClick) {
					event.subtype = 'help-modal';
					event.subcategory = 'Get Help Link Campaign Page';
				}
				if (this.isGetHelpLoggedOutCampaignCCLogoClick) {
					event.subtype = 'help-modal';
					event.subcategory = 'Get Help Link CCLogo Click';
				}
				break;
			case ANALYTICS.ITEM_CLICK:
				event.type = 'click';
				event.guid = guid;
				event.subtype = section;
				content.id = questionId;
				content.name = HelpPopoverUtil.questionEngText[questionId];
				content.category = section;
				content.action = extUrl;
				break;
			case ANALYTICS.FALLBACK:
				event.type = 'api-error';
				event.subtype = 'cvs-help-menu';
				if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
					event.subtype = 'aem-help-menu';
				}
				break;
			case ANALYTICS.NO_DATA:
				event.type = 'api-error';
				event.subtype = 'cvs-help-menu-fallback';
				if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
					event.subtype = 'aem-help-menu-fallback';
				}
				break;
			case ANALYTICS.CLICK:
				event.subcategory = analytics.getEventSubcategoryString();
				event.type = 'click';
				event.subtype = 'help';
				break;
			case ANALYTICS.LINK_CLICK:
				event.type = 'click';
				event.guid = guid;
				event.subtype = section;
				content.id = questionId;
				content.name = HelpPopoverUtil.questionEngText[questionId];
				content.category = 'faq';
				content.action = extUrl;
				break;
		}
		if (AppsUtil.isLoggedOutCampaignExp) {
			event.workflow = ANALYTICS.CAMPAIGN;
			ui.view_type = LOGGED_OUT_CAMPAIGN_ENV_NAME; // eslint-disable-line camelcase
			event.subcategory = CCDUtil.getCampaignWorkflowSubcategory();
			env = {
				name: LOGGED_OUT_CAMPAIGN_ENV_NAME,
				version: URL.getParam('exp'),
			};
		}
		analytics.genericAnalyticEvent({
			event,
			content,
			ui,
			env
		});
	}

	setCallback(id: string, callback: Function) {
		this.callbacks[id] = callback;
	}

	/**
	* to update the config in runtime.
	* arguments defaultQuestionId - default selected question when opened.
	* updatingList - to update config and it is of structure: [{
	*		listId: the list in which the changes are to be done
	*		heading: optional - if heading need to be changed.
	*		compatibilityTags: [] - optional, for adding related questions
	*		installWorkflowTags: [] - optional, for adding related questions
	*		pageType: [] - optional
	* }]
	*/
	updateHelpPopoverConfig(defaultQuestionId: ?string, updatingList: Array<Object> = []) {
		const {config} = this.state;

		if (defaultQuestionId) {
			HelpPopoverUtil.selectedQuestion = defaultQuestionId;
		}

		if (!config.length) {
			this.configUpdateArguments = {defaultQuestionId, updatingList};
			return;
		}

		const mappings = {};
		config.forEach((obj) => {
			mappings[obj.listId] = obj;
		});

		updatingList.forEach((quesList) => {
			const list = mappings[quesList.listId];

			// updating heading
			if (list && quesList.heading) {
				list.heading = quesList.heading;
			}

			// updating questions
			if (list && (quesList.installWorkflowTags || quesList.compatibilityTags || quesList.pageType)) {
				// fetch default tags and set installWorkflowTags, compatibilityTags and pageTags
				const updatedTags = this.getDefaultTags();
				if (quesList.installWorkflowTags) {
					updatedTags.installWorkflowTags = [...updatedTags.installWorkflowTags, ...quesList.installWorkflowTags];
				}
				if (quesList.compatibilityTags) {
					updatedTags.compatibilityTags = [...updatedTags.compatibilityTags, ...quesList.compatibilityTags];
				}
				if (quesList.pageType) {
					updatedTags.pageType = [...updatedTags.pageType, ...quesList.pageType];
				}
				list.listItems = this.filterQuestionsByTags(updatedTags);
			}
		});

		this.setState({config: [...config], defaultSelectedItemId: defaultQuestionId});
	}

	showWithSelectedItem(item: ?string) {
		this.setState({defaultSelectedItemId: item});
		requestAnimationFrame(() => {
			this.show();
		});
	}

	componentDidMount() {
		this.fetchGetHelpQuestions(this.props.locale);
	}

	getContentUrl(config: string, locale: string) {
		if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
			return `${AppsUtil.getAEMDomain()}${getAEMPath(config, locale)}`;
		}
		return `${AppsUtil.getCVSDomain()}${getContentfulPath(config, locale)}`;
	}

	fetchGetHelpQuestions(locale: string) {
		const useNull = (e) => {
			console.log(e);
			return null;
		};
		let plan = AppPickerUtil.getAppPickerPlan();
		if ([CCPP_PLAN, CCPPN_PLAN, ALL_APPS_PLAN, SUBSTANCE_COLLECTION_PLAN, SUBSTANCE_TEXTURING_PLAN].indexOf(plan) === -1) {
			plan = AppsUtil.getSingleAppPostPurchasePlan();
			if (plan) {
				plan = SINGLE_APP_PLAN;
			}
		}
		const workflow = AppsUtil.getPathData().pdpTab;
		let contentfulURl;
		let fallbackURL;
		const selectPlanHelper = () => {
			switch (plan) {
				case ALL_APPS_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfig', locale);
					fallbackURL = '/assets/apps/allAppsAppPicker/en/getHelpFallbackData.json';
					break;
				case CCPP_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfigCcpp', locale);
					fallbackURL = '/assets/apps/ccppAppPicker/en/getHelpFallback.json';
					break;
				case CCPPN_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfigCcppn', locale);
					fallbackURL = fallbackURL = '/assets/apps/ccppnAppPicker/en/getHelpFallback.json';
					break;
				case SUBSTANCE_COLLECTION_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfigSubstanceCollection', locale);
					fallbackURL = fallbackURL = '/assets/apps/substanceCollectionAppPicker/en/getHelpFallback.json';
					break;
				case SUBSTANCE_TEXTURING_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfigSubstanceTexturing', locale);
					fallbackURL = fallbackURL = '/assets/apps/substanceTexturingAppPicker/en/getHelpFallback.json';
					break;
				case SINGLE_APP_PLAN:
					contentfulURl = this.getContentUrl('getHelpConfigSingleApp', locale);
					fallbackURL = '/assets/apps/singleApp/en/getHelpFallback.json';
					break;
			}
		};
		if (workflow === 'campaign') {
			contentfulURl = this.getContentUrl('getHelpConfigCampaign', locale);
			fallbackURL = '/assets/apps/loggedOutCampaign/en/getHelpFallbackData.json';
		}
		else {
			selectPlanHelper();
		}
		const allPromisesArray = [axios.get(contentfulURl, {timeout: 5000}).catch(useNull) ,axios.get(fallbackURL).catch(useNull)];
		Promise.all(allPromisesArray).then(axios.spread((cvsResponse, cvsFallback) => {
			if (cvsResponse === null || AppsUtil.hasFeature('use_get_help_fallback')) {
				if (cvsFallback && cvsFallback.data) {
					this.handleAnalyticsEvent(ANALYTICS.FALLBACK);
					this.createAndSetGetHelpConfig(cvsFallback.data);
					HelpPopoverUtil.setFaqListIsAvailable();
					this.setState({isDataAvailable: true});
				}
				else {
					this.handleAnalyticsEvent(ANALYTICS.NO_DATA);
					this.setState({isDataAvailable: false});
				}
			}
			else {
				this.createAndSetGetHelpConfig(cvsResponse.data);
				HelpPopoverUtil.setFaqListIsAvailable();
				this.setState({isDataAvailable: true});
			}
		}));
	}

	// filter the questions as per the tags
	filterQuestionsByTags(tags: Object) {
		let questionList = [...this.getHelpQuestions];
		Object.keys(tags).forEach((key) => {
			questionList = questionList.filter((ques) => {
				let bool = false;
				tags[key].forEach((tag) => {
					if (typeof this.scenarioTagList[ques.id][key] === 'undefined' || this.scenarioTagList[ques.id][key] === null) {
						bool = true;
					}
					bool = bool || this.scenarioTagList[ques.id][key] && this.scenarioTagList[ques.id][key].indexOf(tag) !== -1;
				});
				return bool;
			});
		});
		return questionList;
	}

	// default tags - all general questions and device matching questions.
	getDefaultTags() {
		return {
			sourceTags: [TAGS.GENERAL, TAGS.SOURCE.CCWEB],
			platformTags: [TAGS.GENERAL, ...this.getPlatformTag()],
			pageType: [TAGS.GENERAL],
			browserTags: [TAGS.GENERAL, this.getBrowserTag()],
			installWorkflowTags: [TAGS.GENERAL],
			compatibilityTags: [TAGS.GENERAL],
			productTags: [TAGS.GENERAL, ...this.getProductTag()]
		};
	}

	getPlatformTag() {
		const platform = TAGS.PLATFORM;
		if (DeviceDetectionUtil.isMacOS()) {
			return [platform.MAC];
		}
		if (DeviceDetectionUtil.isWin()) {
			return [platform.WINDOWS];
		}
		if (DeviceDetectionUtil.isLinux()) {
			return [platform.LINUX];
		}
		if (DeviceDetectionUtil.isChromeOS()) {
			return [platform.CHROMEBOOK];
		}
		if (DeviceDetectionUtil.isMobileOrTab()) {
			return [platform.MOBILE];
		}
		return [];
	}

	getBrowserTag() {
		const browser = TAGS.BROWSER;
		if (DeviceDetectionUtil.isChrome()) {
			return browser.CHROME;
		}
		if (DeviceDetectionUtil.isSafari()) {
			return browser.SAFARI;
		}
		if (DeviceDetectionUtil.isFirefox()) {
			return browser.FIREFOX;
		}
		if (DeviceDetectionUtil.isEdge()) {
			return browser.EDGE;
		}
		if (DeviceDetectionUtil.isInternetExplorer()) {
			return browser.IE;
		}
		return browser.NON_MAJOR_BROWSER;
	}

	getProductTag() {
		return this.getProductIdFromUrl() ? [TAGS.PRODUCT[AppsUtil.getSapCodeFromProductId(this.getProductIdFromUrl())]] : [];
	}

	onGetHelpLinkClick = (e: Object, questId: string) => {
		e.preventDefault();
		if (e.currentTarget.hash && Object.values(GET_HELP_ANSWER_LINKS).includes(e.currentTarget.hash)) {
			switch (e.currentTarget.hash) {
				case GET_HELP_ANSWER_LINKS.OPEN_CHAT_SUPPORT:
					this.handleAnalyticsEvent(ANALYTICS.LINK_CLICK, 'chat', questId);
					this.openChat();
					break;
				case GET_HELP_ANSWER_LINKS.RESTART_DOWNLOAD:
					this.handleAnalyticsEvent(ANALYTICS.LINK_CLICK, 'restart-download', questId);
					this.onRestartDownload();
					break;
				case GET_HELP_ANSWER_LINKS.INCOMPATIBLE_OS_APP_CATALOG:
					this.handleAnalyticsEvent(ANALYTICS.LINK_CLICK, 'view-desktop-apps', questId);
					if (this.callbacks[GET_HELP_CALLBACK.VIEW_DESKTOP_APPS]) {
						this.callbacks[GET_HELP_CALLBACK.VIEW_DESKTOP_APPS]();
					}
					break;
			}
		}
		else {
			let extUrl = e.currentTarget.href;
			const targetHost = e.currentTarget.host;
			let guid;
			if (targetHost.indexOf('adobe.com') > 0) {
				guid = uuid.v4();
				extUrl = AppsUtil.openExternalLink(extUrl, undefined, guid);
			}
			else {
				window.open(extUrl, '_blank');
			}
			this.handleAnalyticsEvent(ANALYTICS.LINK_CLICK, 'link', questId, extUrl, guid);
		}
	}

	modifyAnchors(content: string) {
		const container = document.createElement('div');
		container.innerHTML = content;

		const anchors = container.getElementsByTagName('a');
		for (const anchor of anchors) {
			anchor.target = '_blank';
			anchor.className = 'GetHelpLink';
		}
		return container.innerHTML;
	}

	createAndSetGetHelpConfig(data: Object) {
		let parsedData;
		if (AppsUtil.hasFeature('temp_contentful_to_aem_migration')) {
			parsedData = data.data.nujExperienceHelpMenuByPath.item;
		}
		else {
			parsedData = ContentParser.parse(data).items[0];
		}
		HelpPopoverUtil.fullQuestionList = parsedData.itemList;
		this.scenarioTagList = {};
		this.getHelpQuestions = parsedData.itemList.map((obj) => {
			const quesObj = {};
			quesObj.id = obj.id;
			const options = {
				renderNode: {
					[INLINES.HYPERLINK]: (node, next) => `<a href="${node.data.uri}" class="GetHelpLink" target="_blank">${next(node.content)}</a>`
				}
			};
			quesObj.answerText = AppsUtil.hasFeature('temp_contentful_to_aem_migration') ? this.modifyAnchors(obj.answerText.html) : documentToHtmlString(obj.answerText, options);
			quesObj.answerPlainText = AppsUtil.hasFeature('temp_contentful_to_aem_migration') ? obj.answerText.plaintext : '';
			quesObj.questionText = obj.questionText;
			this.scenarioTagList[obj.id] = {
				sourceTags: obj.sourceTags,
				compatibilityTags: obj.compatibilityTags,
				installWorkflowTags: obj.installWorkflowTags,
				pageType: obj.pageType,
				platformTags: obj.platformTags,
				browserTags: obj.browserTags,
				productTags: obj.productTags
			};
			HelpPopoverUtil.questionEngText[obj.id] = obj.nonLocalisedQuestionText;
			return quesObj;
		});
		const config = [
			{
				listId: LIST_IDS.FAQ_AND_HELP,
				heading: this.props.intl.formatMessage({id: GET_HELP_STRINGS.FAQ_HEADING}),
				listItems: this.filterQuestionsByTags(this.getDefaultTags())
			},
			{
				listId: LIST_IDS.MORE_RESOURCES,
				heading: this.props.intl.formatMessage({id: GET_HELP_STRINGS.MORE_RESOURCES}),
				listItems: [
					{
						id: QUESTION_IDS.CHAT_WITH_SUPPORT,
						questionText: this.props.intl.formatMessage({id: GET_HELP_STRINGS.CHAT_WITH_SUPPORT})
					},
					{
						id: QUESTION_IDS.ASK_SUPPORT_COMMUNITY,
						questionText: this.props.intl.formatMessage({id: GET_HELP_STRINGS.AST_SUPPORT_COMMUNITY})
					}
				]
			}
		];
		this.setState({config}, () => {
			// if any update config call before initial config is updated now.
			if (this.configUpdateArguments) {
				const {defaultQuestionId, updatingList} = this.configUpdateArguments;
				this.updateHelpPopoverConfig(defaultQuestionId, updatingList);
				this.configUpdateArguments = null;
			}
		});
	}

	openChat() {
		window.AdobeMessagingExperienceClient && window.AdobeMessagingExperienceClient.openMessagingWindow({sourceType: 'link'});
		this.helpPopover && this.helpPopover.hide();
	}

	getProductIdFromUrl() {
		if (AppsUtil.isLoggedOutExp()) {
			return AppsUtil.getLoggedOutExpPathData().product;
		}
		return AppsUtil.getCCWebPathData().product;
	}

	isDownloadViaCCD() {
		const productId = this.getProductIdFromUrl();
		const sapcode = AppsUtil.getSapCodeFromProductId(productId);
		return (sapcode === AppPickerUtil.getDefaultDownloadProduct())
			&& (URL.getParam(DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_PARAM) === DOWNLOAD_VIA_CCD_QUERY_PARAM.QUERY_VALUE);
	}

	onRestartDownload() {
		const sapcode = AppsUtil.getSapCodeFromProductId(this.getProductIdFromUrl());
		if (!sapcode) {
			return;
		}
		DownloadUtil.downloadProduct({sapcode, hasThor:false, isAllApps: this.isDownloadViaCCD()}).then((workflow) => {
			if (workflow === DownloadWorkflow.Kaizen || workflow === DownloadWorkflow.KaizenOldVersion) {
				const isCoachmarkAlreadyPresent = !(document.querySelector('#popUpInstaller') === null || document.querySelector('#popUpInstaller').style.display === 'none');
				if (!isCoachmarkAlreadyPresent) {
					CSSUtil.showDownloadCoachMarkWithBounce(<DownloadCoachMark isAllAppsPicker={true} intl={this.props.intl}/>, 1500, () => {}, true);
				}
			}
		});
	}

	replacePlaceHolder(link: Object) {
		const productId = this.getProductIdFromUrl();
		const sapcode = AppsUtil.getSapCodeFromProductId(productId);
		if (sapcode) {
			switch (link.firstChild.data) {
				case PLACEHOLDER_STRINGS.FILENAME: {
					const fileName = DownloadUtil.getDownloadFileName(this.isDownloadViaCCD() ? 'KCCC' : sapcode);
					link.outerHTML = `<span class=filename-span >'${fileName}'</span>`;
					break;
				}
				case PLACEHOLDER_STRINGS.APPNAME: {
					const displayName = AppsUtil.getProductDisplayName(productId) || AppPickerUtil.getDisplayName(sapcode);
					link.outerHTML = `<span>${displayName}</span>`;
					break;
				}
			}
		}
	}

	onClickOnQuestion(ques: Object) {
		let extUrl;
		switch (ques.id) {
			case QUESTION_IDS.CHAT_WITH_SUPPORT:
				this.openChat();
				this.handleAnalyticsEvent(ANALYTICS.ITEM_CLICK, 'resource', ques.id);
				break;
			case QUESTION_IDS.ASK_SUPPORT_COMMUNITY: {
				const guid = uuid.v4();
				extUrl = AppsUtil.openExternalLink(getLocalizedURL(getConfigApps('EXTERNAL_URLS').COMMUNITY_URL, this.props.locale), undefined, guid);
				this.handleAnalyticsEvent(ANALYTICS.ITEM_CLICK, 'resource', ques.id, extUrl, guid);
				break;
			}
			default:
				this.handleAnalyticsEvent(ANALYTICS.ITEM_CLICK, 'faq', ques.id);
		}
		HelpPopoverUtil.selectedQuestion = ques.id;
		requestAnimationFrame(() => {
			/*
				Add callback for all the links that come in the answer text, and,
				replace placeholders by appropraite text.
			*/
			const getHelpLinks = document.getElementsByClassName('GetHelpLink');
			if (getHelpLinks.length > 0) {
				const placHolderLinks = [];
				for (const link of getHelpLinks) {
					if (link.hash === GET_HELP_ANSWER_LINKS.PLACEHOLDER) {
						placHolderLinks.push(link);
					}
					else {
						CSSUtil.addEventListenerToElem(
							link,
							'click',
							(e) => {
								this.onGetHelpLinkClick(e, ques.id);
							}
						);
					}
				}
				placHolderLinks.forEach(link => this.replacePlaceHolder(link));
			}
		});
	}

	onHide() {
		const defaultSelectedItemId = this.state;
		if (defaultSelectedItemId) {
			this.setState({defaultSelectedItemId: null, shouldFocusFirstListItem: false});
		}
	}

	onShow() {
		this.handleAnalyticsEvent(ANALYTICS.RENDER);
		this.isInactivity = false;
		this.isCCLogoClick = false;
		this.isFooterLinkClick = false;
		this.isGetHelpDownloadPageClick = false;
		this.isGetHelpLoggedOutCampaignPageClick = false;
		this.isGetHelpLoggedOutCampaignCCLogoClick = false;
	}

	onClick() {
		this.handleAnalyticsEvent(ANALYTICS.CLICK);
	}

	shouldToggleMenu = (event: Object) => {
		if (event.key === 'Enter') {
			this.show(undefined, true);
		}
		else if (event.key === 'Esc' || event.key === 'Escape') {
			this.hide();
		}
	}

	render() {
		const {showAutoDownload, shouldFocusFirstListItem, isDataAvailable} = this.state;
		const showProp = {};
		if (showAutoDownload) {
			showProp.show = true;
		}
		if (!isDataAvailable) {
			return null;
		}
		return (
			<div className='get-help-container' onClick={() => this.onClick()}>
				<OverlayTrigger
					id='getHelp-drop-down'
					ref={r => this.helpPopover = r}
					trigger='click'
					placement='bottom'
					onHide={() => this.onHide()}
					onShow={() => this.onShow()}
					disabled={!this.state.config.length}
					crossOffset={-100}
					{...showProp}>
					<div className='helpNav' tabIndex={0} onKeyDown={this.shouldToggleMenu}>
						<Help size='S' />
						<label className='helpLabel'>{this.props.intl.formatMessage({id: GET_HELP_STRINGS.GET_HELP})}</label>
					</div>
					{showAutoDownload ? <AutoDownload history={this.props.history} environment={this.props.environment} hide={() => {this.hide();}}/>
						: <GetHelp
							config={this.state.config}
							className={'getHelpPopover'}
							onSelect={(ques) => {this.onClickOnQuestion(ques);}}
							defaultSelectedItemId={this.state.defaultSelectedItemId}
							feedback={Feedback}
							shouldFocusFirstListItem={shouldFocusFirstListItem}/>}
				</OverlayTrigger>
			</div>
		);
	}
}
